// All SEO content data — competitors, use-cases, schema constants.
// Edit here once, rendered consistently across pages, sitemap, and schema.

export const SITE = {
  name: "BreakItDown",
  domain: "https://break-it-down.net",
  tagline: "Break big tasks down. Get them done.",
  description:
    "BreakItDown is an AI-powered task management app that automatically splits big tasks into smaller, actionable subtasks. Built for focused individuals, freelancers, and small teams.",
  founder: { name: "Charlotte Bryan", url: "https://charlottebryan.com.au/" },
  ogImage: "https://break-it-down.net/og.png",
};

export const COMPETITORS = [
  {
    slug: "freedcamp",
    name: "Freedcamp",
    tagline: "Free project management with optional paid add-ons",
    positioning:
      "Freedcamp is a long-standing free project management suite, but its workflow is built around classic boards, lists, and modules rather than AI-assisted breakdowns.",
    differences: [
      "BreakItDown auto-breaks a single big task into 4–6 subtasks with Claude AI — Freedcamp leaves the breakdown to you.",
      "BreakItDown ships with a built-in Pomodoro-style focus timer per task; Freedcamp focuses on shared boards.",
      "BreakItDown is single-purpose and opinionated; Freedcamp is broad with many modules and add-ons.",
    ],
    table: [
      ["Free tier", "Yes (entire app)", "Yes (core features)"],
      ["AI task breakdown", "Yes — Claude Sonnet 4.5", "No"],
      ["Built-in Pomodoro timer", "Yes (per task, sound cues)", "No"],
      ["Install to home screen (PWA)", "Yes", "No"],
      ["Public read-only share links", "Yes", "Yes (paid)"],
      ["Best for", "Focused solo work, ADHD, small teams", "Multi-project teams"],
    ],
  },
  {
    slug: "clickup",
    name: "ClickUp",
    tagline: "All-in-one productivity platform with heavy features",
    positioning:
      "ClickUp is a powerful enterprise-grade workspace, but its surface area can feel overwhelming for solo users or small teams that just want to plan, focus, and ship.",
    differences: [
      "BreakItDown is intentionally small: one list, one timer, one AI breakdown — no docs/whiteboards/forms to learn.",
      "ClickUp has a steep onboarding; BreakItDown is usable in 30 seconds.",
      "BreakItDown's AI breakdown is enabled out of the box; ClickUp's AI is a paid add-on.",
    ],
    table: [
      ["Free tier", "Yes (entire app)", "Yes (limited)"],
      ["AI task breakdown", "Yes — included", "Yes — paid add-on"],
      ["Setup time", "30 seconds", "30+ minutes"],
      ["Learning curve", "Tiny", "Steep"],
      ["Best for", "Solo focus & small teams", "Large teams, complex orgs"],
    ],
  },
  {
    slug: "asana",
    name: "Asana",
    tagline: "Team work management with timelines & portfolios",
    positioning:
      "Asana excels at coordinating cross-functional teams with projects, timelines, and goals — but it's not designed for the day-to-day mental load of breaking your own work down.",
    differences: [
      "BreakItDown lives at the individual-task level with AI breakdowns and a focus timer; Asana lives at the project-coordination level.",
      "BreakItDown's focus mode hides everything except the task you're working on; Asana keeps you in the team context.",
      "BreakItDown is free; Asana's best features sit behind paid tiers.",
    ],
    table: [
      ["Free tier", "Yes (entire app)", "Yes (limited)"],
      ["AI task breakdown", "Yes", "Limited (Asana Intelligence)"],
      ["Focus mode + timer", "Yes", "No"],
      ["Install to home screen (PWA)", "Yes", "Mobile app required"],
      ["Best for", "Personal & ADHD focus", "Cross-team coordination"],
    ],
  },
  {
    slug: "trello",
    name: "Trello",
    tagline: "Visual kanban boards with cards",
    positioning:
      "Trello is a classic kanban tool — great for visualising flow, but cards stay flat. You still have to decompose your own work mentally.",
    differences: [
      "BreakItDown turns one fuzzy task into a checklist automatically; Trello expects you to write each step yourself.",
      "BreakItDown has an opinionated next-step view; Trello shows you everything at once.",
      "BreakItDown ships with timer + sound cues for focus sessions; Trello has no built-in focus tools.",
    ],
    table: [
      ["AI task breakdown", "Yes", "No"],
      ["Focus timer", "Built-in", "Third-party Power-Ups"],
      ["Best for", "Deep work + ADHD-friendly", "Visual workflows, teams"],
    ],
  },
  {
    slug: "monday",
    name: "Monday",
    tagline: "Visual work platform for teams",
    positioning:
      "Monday (monday.com) is a colourful visual workspace for cross-functional teams, but it's expensive and overkill for the 'help me think through what to do next' problem.",
    differences: [
      "BreakItDown is free forever; Monday starts at a per-seat monthly fee.",
      "BreakItDown's AI breakdown is its core feature, not an add-on.",
      "BreakItDown has no admin/permissions overhead — open it and go.",
    ],
    table: [
      ["Free tier", "Yes (entire app)", "Yes (very limited)"],
      ["AI task breakdown", "Yes — Claude", "Limited"],
      ["Pricing", "Free", "Paid per seat"],
      ["Best for", "Solo & small teams", "Enterprise visual workflows"],
    ],
  },
];

export const USE_CASES = [
  {
    slug: "adhd-productivity",
    h1: "Task management for ADHD: break overwhelm into next steps",
    title: "ADHD Productivity App — Break Big Tasks Down | BreakItDown",
    description:
      "BreakItDown is an ADHD-friendly task manager that auto-breaks big tasks into bite-sized subtasks, with focus mode and sound cues to keep you on track.",
    keywords: ["adhd productivity", "adhd task management", "adhd planner", "task breakdown ADHD"],
    intro:
      "If you have ADHD, the gap between 'I should do X' and 'okay where do I start?' is where everything stalls. BreakItDown closes that gap: type the big task, hit Break Down, and Claude AI returns the 4–6 concrete next steps so you can just pick one and go.",
    bullets: [
      "AI breakdown turns 'plan offsite' into specific subtasks in 3 seconds.",
      "Focus mode hides everything except the task you're working on.",
      "Sound cues at 30 seconds and the last 10 — perfect for time blindness.",
      "Inline edits and instant keyboard shortcuts — fewer keystrokes when motivation is thin.",
    ],
  },
  {
    slug: "task-management-software",
    h1: "Modern task management software — AI-powered, free, focused",
    title: "Best Task Management Software 2026 — Free + AI | BreakItDown",
    description:
      "Looking for task management software that actually helps you think? BreakItDown adds AI task breakdown, a focus timer, and one-tap home-screen install — all free.",
    keywords: ["task management software", "best task manager 2026", "free task management app"],
    intro:
      "Most task managers are just glorified lists. BreakItDown adds the missing layer: AI that converts vague intentions into actionable subtasks, plus a focus timer, sound cues, install-to-home-screen, and read-only share links — all in a single-page, no-bloat interface.",
    bullets: [
      "Free forever — no paywall.",
      "Claude Sonnet 4.5 generates subtasks from any title + optional description.",
      "Drag-and-drop reorder, priority, due dates, recurring tasks.",
      "Public share links for any task — read-only, revoke anytime.",
    ],
  },
  {
    slug: "workflow-software",
    h1: "Workflow software for solo and small teams that ship",
    title: "Workflow Software That Stays Out of Your Way | BreakItDown",
    description:
      "BreakItDown is workflow software for people who want to ship, not configure. AI breaks tasks down, a timer keeps you focused, and the interface stays out of your way.",
    keywords: ["workflow software", "personal workflow app", "lightweight workflow tool"],
    intro:
      "Workflow software usually means weeks of setup. BreakItDown is the opposite: open it, type a task, click Break Down, and you have a working flow. No statuses, swimlanes, or admin to configure first.",
    bullets: [
      "Zero-setup workflow — start in 30 seconds.",
      "Work and Personal tabs cleanly separate contexts.",
      "Recurring tasks for repeating workflows.",
      "Public share links to hand off work without giving an account.",
    ],
  },
  {
    slug: "project-management",
    h1: "Lightweight project management for solo work and small teams",
    title: "Lightweight Project Management — Free & AI-Assisted | BreakItDown",
    description:
      "BreakItDown is project management without the bloat. Plan, break work down with AI, focus with a timer, and ship. Free.",
    keywords: ["project management", "lightweight project management", "solo project manager"],
    intro:
      "Big project management tools are great for big teams. For everyone else, they slow you down. BreakItDown is a minimalist project manager built on one idea: a fuzzy task → a clear checklist → done.",
    bullets: [
      "AI subtask breakdown — instant project decomposition.",
      "Priority + due dates + recurrence cover 95% of project planning needs.",
      "Single-user fully private; share read-only links per task.",
    ],
  },
  {
    slug: "ai-productivity-tools",
    h1: "AI productivity tools that actually do the planning for you",
    title: "AI Productivity Tools — BreakItDown Uses Claude to Plan Your Day",
    description:
      "BreakItDown is one of the most useful AI productivity tools in 2026 — it uses Claude Sonnet 4.5 to turn vague tasks into actionable subtasks instantly.",
    keywords: ["AI productivity tools", "Claude AI for productivity", "AI task planner"],
    intro:
      "Most 'AI productivity' apps just bolt a chatbot onto a list. BreakItDown actually uses Claude Sonnet 4.5 to do the cognitive work — turning vague intentions into specific subtasks, daily briefings into a notification, and context (via a description field) into smarter plans.",
    bullets: [
      "Claude Sonnet 4.5 task breakdown — context-aware.",
      "Claude Haiku 4.5 for short tasks — 4× cheaper, still excellent.",
      "Daily AI briefing — top 3 tasks pushed to your browser.",
    ],
  },
  {
    slug: "kanban-software",
    h1: "Kanban software for people who want to ship, not move cards",
    title: "Kanban Software Alternative — Focus + AI | BreakItDown",
    description:
      "Tired of dragging cards across columns? BreakItDown is a kanban alternative that helps you finish tasks instead of organising them.",
    keywords: ["kanban software", "kanban alternative", "lightweight kanban"],
    intro:
      "BreakItDown isn't classic kanban — it's the opposite. Instead of moving cards through columns, you check off subtasks as you finish them and they disappear from the list. The result: a list that gets shorter the more you do.",
    bullets: [
      "Active list shrinks as you complete tasks (no archive overhead).",
      "Completed archive available when you want to celebrate.",
      "Work/Personal tabs replace columns — fewer decisions.",
    ],
  },
  {
    slug: "remote-team-management",
    h1: "Remote team management without the meetings",
    title: "Remote Team Management — Async-First with BreakItDown",
    description:
      "BreakItDown supports remote teams with read-only public share links, so anyone on the team can see exactly what a teammate is breaking down — without inviting them to the tool.",
    keywords: ["remote team management", "async remote work", "remote work tools"],
    intro:
      "Async-first teams need a fast way to share what you're working on without yet another invite or seat. BreakItDown gives every task a read-only public link — paste it in Slack and your team sees the breakdown, progress, and subtasks. No sign-in needed.",
    bullets: [
      "Per-task public share links — revoke anytime.",
      "No seat-based pricing — invite zero people, share everything.",
      "Focus mode for the deep work between syncs.",
    ],
  },
  {
    slug: "freelancer-productivity",
    h1: "Freelancer productivity app for client work",
    title: "Freelancer Productivity App — Plan & Ship | BreakItDown",
    description:
      "BreakItDown is a freelancer productivity app — break a client brief into subtasks, time-box with a timer, and share progress via a public link.",
    keywords: ["freelancer productivity", "freelancer task manager", "freelancer planner"],
    intro:
      "Freelancers juggle context: every client is a different brain space. BreakItDown is a single dashboard where you can dump a client brief, hit Break Down, time-box with the focus timer, and share a public progress link without giving the client an account.",
    bullets: [
      "AI breakdown — perfect for vague client briefs.",
      "Per-task focus timer — easy time-boxing.",
      "Public share link per task — client-facing read-only progress.",
    ],
  },
  {
    slug: "agile-workflow-management",
    h1: "Agile workflow management for tiny teams",
    title: "Agile Workflow Management — Lightweight & Free | BreakItDown",
    description:
      "BreakItDown is an agile workflow tool for tiny teams. Break stories into subtasks with AI, run focused sprints with a built-in timer, and ship faster.",
    keywords: ["agile workflow management", "agile tools small team", "scrum lightweight"],
    intro:
      "Most agile tools are built for 30+ person teams. BreakItDown is built for the 1–5 person crew that just needs to ship: AI story breakdown, time-boxed focus, recurring rituals, and a clean list that gets shorter the more you do.",
    bullets: [
      "AI breaks user stories into engineering subtasks.",
      "Recurring tasks for standups, retros, releases.",
      "No ceremonies, no plugin sprawl.",
    ],
  },
  {
    slug: "focus-systems",
    h1: "A focus system that actually keeps you focused",
    title: "Focus System — Pomodoro + AI Breakdown | BreakItDown",
    description:
      "BreakItDown is a focus system for deep work — Pomodoro timer, audio cues at 30s and 10s, fullscreen focus mode, and AI subtask breakdown.",
    keywords: ["focus system", "deep work app", "pomodoro alternative"],
    intro:
      "BreakItDown is a focus system disguised as a task list. Pick a task, hit Focus, and the rest of the app disappears. The big timer counts down, soft beeps at 30s, ticking countdown at 10s, red pulsing display, and a triple chime when you're done.",
    bullets: [
      "Fullscreen focus mode.",
      "Audio cues at 30s and 10s (Web Audio — no assets).",
      "Auto-fade completed tasks so the list stays motivating.",
    ],
  },
];

export const ALL_FAQS = {
  homepage: [
    {
      q: "What is BreakItDown?",
      a: "BreakItDown is a free AI-powered task management app that automatically splits big tasks into 4–6 actionable subtasks using Claude Sonnet 4.5. It also includes a focus timer with sound cues, install-to-home-screen (PWA), public share links, and a daily briefing.",
    },
    {
      q: "Is BreakItDown free?",
      a: "Yes — BreakItDown is free with no paywall or premium tier. The AI breakdown is included.",
    },
    {
      q: "Does BreakItDown work offline?",
      a: "BreakItDown is a web app that requires an internet connection. A service worker is registered so that scheduled daily briefings can fire even when the tab is closed.",
    },
    {
      q: "How does the AI task breakdown work?",
      a: "Type a task title (and optional description for context) and click 'Break down'. BreakItDown calls Claude (Sonnet 4.5 for longer tasks, Haiku 4.5 for short ones) with your task, priority, and description, and returns 4–6 short actionable subtasks as a checklist.",
    },
    {
      q: "Can I share a task with someone who doesn't have an account?",
      a: "Yes. Click the Share button on any task to generate a read-only public link. Anyone with the link can view the task, description, and subtasks without signing in. You can revoke the link any time.",
    },
    {
      q: "What is the best free alternative to ClickUp or Asana?",
      a: "BreakItDown is a strong free alternative to ClickUp, Asana, Monday, Trello, and Freedcamp — especially if you want AI task breakdown, a built-in focus timer, and a minimal UI without the enterprise overhead.",
    },
    {
      q: "Can I install BreakItDown like an app on my phone or computer?",
      a: "Yes. On iPhone open the page in Safari, tap Share, then 'Add to Home Screen'. On Android Chrome use the ⋮ menu → 'Install app'. On desktop Chrome/Edge click the install icon in the address bar. Installed PWAs stay signed in and open like a native app.",
    },
  ],
  pricing: [
    {
      q: "How much does BreakItDown cost?",
      a: "BreakItDown is 100% free. There is no premium tier or paid add-on.",
    },
    {
      q: "Is there a paid plan?",
      a: "No paid plan — every feature, including AI task breakdown, focus mode, and install-to-home-screen, is included for free.",
    },
    {
      q: "Are there any limits?",
      a: "Generous fair-use limits apply to AI breakdowns (per-user daily cap, identical re-runs cached for 24h) to keep the service free for everyone.",
    },
  ],
};

export const CHANGELOG = [
  {
    version: "1.0",
    date: "2026-05-18",
    title: "PWA install + cost optimisation",
    items: [
      "Install BreakItDown to your home screen — works on iPhone, Android, and desktop.",
      "Sessions extended to 60 days so installed PWA users don't keep re-signing in.",
      "AI breakdown auto-uses Claude Haiku for short tasks (4× cheaper) and caches identical re-runs for 24 hours.",
      "Voice dictation removed to keep the service free for everyone.",
    ],
  },
  {
    version: "0.9",
    date: "2026-05-18",
    title: "Real task lists + drag-to-reorder + share badge",
    items: [
      "Multiple named task lists, each tagged Work or Personal.",
      "Drag list rows in the sidebar to reorder.",
      "Public share links now carry a 'Made with BreakItDown' badge for friendly discovery.",
    ],
  },
  {
    version: "0.8",
    date: "2026-02-18",
    title: "Inline title edit",
    items: ["Click any task title to edit in place — Enter saves, Esc cancels."],
  },
  {
    version: "0.7",
    date: "2026-02-18",
    title: "Share + Auto-save + Service worker briefing",
    items: [
      "Public read-only share links per task.",
      "Edit dialog now auto-saves while you type.",
      "Service worker schedules the daily briefing even when the tab is closed.",
    ],
  },
  {
    version: "0.6",
    date: "2026-02-18",
    title: "Settings + focus polish",
    items: [
      "Settings dialog: name, briefing time, default duration.",
      "Brute-force lockout (5 attempts → 15 min).",
      "Focus mode: 30s warn, 10s countdown, red pulse, completion chime.",
      "Edit dialog with description (feeds the AI breakdown).",
    ],
  },
  {
    version: "0.5",
    date: "2026-02-18",
    title: "Multi-account auth",
    items: ["JWT email/password + Google sign-in.", "All tasks scoped per user."],
  },
  {
    version: "0.4",
    date: "2026-02-18",
    title: "Focus mode + keyboard shortcuts",
    items: ["Fullscreen single-task focus.", "Shortcuts: /, b, space, Esc."],
  },
];
